import React, { ChangeEvent } from 'react'
import { Autocomplete as MUIAutocomplete, TextField } from '@carsys/ui'
import { Chip } from '@mui/material'
import { FieldInputProps, FieldMetaProps, FieldValidator, useField } from 'formik'
import { AutocompleteRenderGetTagProps, AutocompleteRenderInputParams, createFilterOptions } from '@mui/material/Autocomplete'
import { isInvalid } from './utils'
import { useTranslation } from 'react-i18next'
import { useFormSection } from './FormSection'

const filter = createFilterOptions()

export interface AutocompleteOption {
  value: any
  text: string
  label?: string
}

interface AutocompleteProps extends Omit<React.ComponentProps<typeof MUIAutocomplete>, 'renderInput' | 'options'> {
  name: string
  label?: string
  required?: boolean
  validate?: FieldValidator
  renderInput?: ({
    params,
    input,
    meta,
    helperText
  }: {
    params: AutocompleteRenderInputParams
    input: FieldInputProps<any>
    meta: FieldMetaProps<any>
    helperText?: string
  }) => React.ReactNode
  options?: AutocompleteOption[]
  helperText?: string
  onChange?: (value: any) => void
  getOptionLabel?: (option: any) => string
}

export const Autocomplete = (props: AutocompleteProps) => {
  const getDefaultOptionLabel = (value: any) => {
    const foundOption = props.options?.find((option: any) => option.value === value)
    return foundOption?.text || value?.text || value
  }

  const {
    name,
    validate,
    options = [],
    placeholder,
    label,
    helperText,
    renderInput,
    onChange,
    getOptionLabel = getDefaultOptionLabel,
    freeSolo,
    multiple,
    ...otherProps
  } = props

  const { t } = useTranslation()
  const [input, meta, helpers] = useField({ name, validate })
  const formSection = useFormSection()

  const changeHandler = (_: ChangeEvent<{}>, data: any) => {
    const value = Array.isArray(data) ? data.map(item => item?.value || item) : data?.value || data || ''
    helpers.setValue(value)

    if (onChange) {
      onChange(value)
    }
  }

  const renderTags = (values: any[], getTagProps: AutocompleteRenderGetTagProps) => {
    if (multiple) {
      return values.map((value, index) => {
        const correctLabel = getOptionLabel(value)
        return <Chip size='small' label={correctLabel} {...getTagProps({ index })} />
      })
    }
    return values
  }

  const requiredValidation = multiple && props.required ? input.value?.length === 0 : !!props.required

  const freeSoloProps = freeSolo
    ? {
        selectOnFocus: true,
        clearOnBlur: true,
        handleHomeEndKeys: true,
        freeSolo: true
      }
    : {}

  return (
    <MUIAutocomplete
      disabled={formSection.disabled}
      id={input.name}
      options={options}
      renderTags={renderTags}
      isOptionEqualToValue={(option: any, value) => option.value === value || option.label === value || value === ''}
      filterOptions={
        freeSolo
          ? (options, params) => {
              const filtered = filter(options, params)
              const { inputValue } = params
              const isExisting = options.some((option: any) => inputValue === option.value)
              if (inputValue !== '' && !isExisting) {
                filtered.push({
                  value: inputValue,
                  text: inputValue,
                  label: `${t('global.add')} "${inputValue}"`
                })
              }
              return filtered
            }
          : undefined
      }
      renderInput={params =>
        renderInput ? (
          renderInput({ params, input, meta, helperText })
        ) : (
          <TextField
            error={isInvalid(meta)}
            helperText={isInvalid(meta) ? meta.error : helperText}
            variant='outlined'
            label={label}
            placeholder={placeholder}
            required={requiredValidation}
            {...params}
          />
        )
      }
      getOptionLabel={getOptionLabel}
      {...input}
      onChange={changeHandler}
      multiple={multiple}
      {...freeSoloProps}
      {...otherProps}
    />
  )
}
